import React, { useCallback, useEffect } from "react";
import classNames from "classnames";
import { PlaidConnection } from "@common/types/plaidConnection";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { Entity } from "@common/types/entity";
import { useFinancialAccountEntitySelection } from "@hooks/useFinancialAccountEntitySelection";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import type { ButtonSize } from "@design-system/Button/Button";
import { ChildrenProps } from "../../../types";
import { useCommands } from "../../commands/useCommands";
import { CommandsDirectory } from "../../commands/lib";
import { isValidAccount } from "./helpers";
import { useAccountsConnectionContext } from "./AccountsConnectionContext";
import { AccountConnectionRow } from "./AccountConnectionRow";
import styles from "./AccountsConnectionWidget.module.scss";

interface AccountsConnectionOnboardingWidgetProps extends ChildrenProps {
    heading: React.ReactNode;
    emptyConnectPrompt?: React.ReactNode;
    connectPrompt?: React.ReactNode;
    accounts: IntegrationAccount[];
    connection?: PlaidConnection;
    onChangeAccountType?(isBusiness: boolean): Promise<void>;
    showAddAccount?: boolean;
    onAddAccount?: () => void;
    onAddAccountComplete?: () => void;
    defaultEntity?: Entity;
    beforeConnect?: () => Promise<void>;
    buttonSize?: Exclude<ButtonSize, "xs">;
}

export const AccountsConnectionOnboardingWidget: React.FC<
    AccountsConnectionOnboardingWidgetProps
> = ({
    heading,
    accounts,
    connection,
    emptyConnectPrompt,
    connectPrompt,
    onChangeAccountType,
    showAddAccount,
    onAddAccount,
    onAddAccountComplete,
    defaultEntity,
    beforeConnect,
    buttonSize = "md",
}) => {
    const { open: openCommands, isOpen } = useCommands();
    const { showBusinessEntitySelection, showPersonalEntitySelection } =
        useFinancialAccountEntitySelection();
    const entitySelection =
        (showBusinessEntitySelection && !defaultEntity?.isBusiness) ||
        showPersonalEntitySelection;
    const cols = entitySelection ? 3 : 2;
    const { showAccountTypeWarning } = useAccountsConnectionContext();
    const hasError =
        showAccountTypeWarning && accounts.some((a) => !isValidAccount(a));

    useEffect(() => {
        if (isOpen) {
            onAddAccount?.();
        } else {
            onAddAccountComplete?.();
        }
    }, [onAddAccountComplete, onAddAccount, isOpen]);

    useEffect(() => {
        if (showAddAccount) {
            openCommands(CommandsDirectory.ONBOARDING, { defaultEntity });
        }
    }, [showAddAccount, openCommands, defaultEntity]);

    const handleAddAccountClick = useCallback(async () => {
        await beforeConnect?.();
        openCommands(CommandsDirectory.ONBOARDING, { defaultEntity });
    }, [beforeConnect, openCommands, defaultEntity]);

    return (
        <table
            className={classNames("table", styles.root, {
                [styles.error]: hasError,
            })}
        >
            <thead>
                <tr>
                    <th colSpan={cols} className="p-0">
                        <div className={styles.header}>
                            <header>{heading}</header>
                        </div>
                    </th>
                </tr>
                {entitySelection && accounts.length > 0 && (
                    <tr
                        className={classNames(
                            "small",
                            "text-grey",
                            styles.headerLabels,
                        )}
                    >
                        <th>Account</th>
                        <th className={styles.classification}>Type</th>
                        <th>Entity / Business</th>
                    </tr>
                )}
            </thead>

            <tbody>
                {accounts.length > 0 ? (
                    <>
                        {accounts.map(
                            (account) =>
                                account.financialAccount && (
                                    <AccountConnectionRow
                                        key={account.id}
                                        integrationAccount={account}
                                        financialAccount={
                                            account.financialAccount
                                        }
                                        connection={connection}
                                        onChangeAccountType={
                                            onChangeAccountType
                                        }
                                        defaultEntity={defaultEntity}
                                    />
                                ),
                        )}
                        <tr>
                            <td colSpan={cols}>
                                <ButtonWithLoader
                                    loading={false}
                                    onClick={handleAddAccountClick}
                                    variant="primary"
                                    size={buttonSize}
                                    disabled={false}
                                >
                                    {connectPrompt}
                                </ButtonWithLoader>
                            </td>
                        </tr>
                    </>
                ) : (
                    <tr>
                        <td colSpan={cols} className="p-0">
                            <div className={styles.empty}>
                                <ButtonWithLoader
                                    loading={false}
                                    onClick={handleAddAccountClick}
                                    variant="primary"
                                    size={buttonSize}
                                    disabled={false}
                                >
                                    {emptyConnectPrompt}
                                </ButtonWithLoader>
                            </div>
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};
